import { useState, useEffect } from 'react';
import ChartImages from '../user/components/BasicChart/ChartImages';
import OtherCharts from '../user/components/BasicChart/OtherCharts';
import PlanetsTable from '../user/components/BasicChart/PlanteryPositions';
import DrishtiAspectsPage from '../user/components/DrishtiAspects';
import LagnaLords from '../user/components/LagnaLords';
import PlanetaryTransitAspect from '../user/components/PlanetaryTransitAspect';
import PlanetAwastha from '../user/components/PlanetAwastha';
import PlanetaryPosition from '../user/components/PlanteryPosition';
import PlanetaryTransit from '../user/components/PlanteryTransit';
import NavamsaTable from '../user/components/BasicChart/NavamsaTable';
import DasamsaChart from '../user/components/BasicChart/DasamsaTable';
import { useUsersQuery } from '@/store/api/usersAPi';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import VimshottariDasha from '../user/components/Vimshottari';
import Prediction from '../user/components/Predictions';
import Loader from '@/components/Loader';
import { TabSlider } from '@/components/ui/TabSlider';
import { ChevronLeft } from 'lucide-react';
import GenPrediction from '../user/components/GenPredictions';
import Ashtakvarga from '../user/components/Ashtakvarga';
import Strengths from '../user/components/Strengths';
import { useGetBirthDetailsQuery } from '@/store/api/BasicChart/BirthDetailsApi';
import { skipToken } from '@reduxjs/toolkit/query';

interface VimshottariDashaProps {
  userId: string;
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? dateStr
      : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const formatTime = (timeStr?: string) => {
  if (!timeStr) return '';
  try {
    const [h, m] = timeStr.split(':');
    if (!h || !m) return timeStr;
    const d = new Date();
    d.setHours(parseInt(h, 10), parseInt(m, 10));
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch {
    return timeStr;
  }
};

const formatTimezone = (tz?: string) => {
  if (!tz) return '';
  const match = tz.match(/\((GMT[+-]\d+(\.\d+)?)\)/);
  return match ? match[1] : tz;
};

export const UserDetailsPage = () => {
  const [params] = useSearchParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const userId = params.get('userId') ?? undefined;

  const [loading, setLoading] = useState(false);

  const [activeView, setActiveView] = useState<
    | 'astro'
    | 'basic'
    | 'vimshottari'
    | 'Predictions'
    | 'General Predictions'
    | 'Ashtakvarga'
    | 'otherCharts'
    | 'strengths'
  >('basic');

  const user = state?.user;

  const birthDetails = user?.meta
    ? {
        date: user.meta.dateOfBirth,
        time: user.meta.timeOfBirth,
        place: user.meta.placeOfBirth,
        fullName: user.fullName,
        gender: user.meta.gender ?? 'Male',
      }
    : null;

  const {
    data: birthApiData,
    isLoading: isBirthLoading,
    error,
  } = useGetBirthDetailsQuery(userId ? { userId } : skipToken);

  const apiData = user?.meta
    ? {
        dob: user.meta.dateOfBirth,
        tob: user.meta.timeOfBirth,
        birthplace: user.meta.placeOfBirth,
        lat: user.meta.latitude,
        lng: user.meta.longitude,
        timezone: user.meta.timezone,
      }
    : null;
  const fetchUserType = user.userType;
  useEffect(() => {
    if (loading) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [loading]);

  useEffect(() => {
    if (activeView === 'Predictions') {
      setLoading(false);
    }
  }, [activeView]);

  if (!user || !userId || isBirthLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <>
      {isBirthLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <Loader />
        </div>
      )}

      {/* {loading && activeView === 'Predictions' && (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <Loader />
      </div>
    )}
    */}

      <div className="flex items-center px-4 mb-4">
        <button
          onClick={() => navigate(-1)}
          className="
        flex items-center gap-2
        px-5 py-2
        rounded-full
        font-semibold
        font-spectral
        text-white
        bg-gradient-to-r from-[#082261] to-[#34065c]
        shadow-lg
        hover:scale-105
        transition-all
        duration-300
      "
        >
          <div>
            <ChevronLeft size={16} />
          </div>
        </button>
      </div>

      <div className="space-y-8 view-table-only">
        {/* User Header Section */}
        <div className="flex flex-col items-center justify-center space-y-6">
          <div className="flex flex-col items-center justify-center space-y-3">
            <p className="font-30 font-spectral font-bold text-center tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-[#082261] to-[#34065c] uppercase">
              {birthApiData?.birthDetails?.fullName || birthDetails?.fullName || user?.fullName}
            </p>
            {(birthApiData?.dateOfBirth ||
              birthApiData?.timeOfBirth ||
              birthApiData?.placeOfBirth ||
              birthDetails?.date) && (
              <div className="flex flex-wrap justify-center gap-3 md:gap-4 text-[#082261] font-lato text-sm md:text-base font-medium">
                {(birthApiData?.dateOfBirth || birthDetails?.date) && (
                  <div className="bg-white/80 px-5 py-2.5 rounded-full shadow-[0_2px_10px_rgba(45,170,253,0.15)] border border-[#2DAAFD]/30 flex items-center gap-2 hover:shadow-[0_4px_15px_rgba(45,170,253,0.25)] transition-all duration-300">
                    <span className="text-gray-500 text-xs font-bold uppercase tracking-wider">
                      DOB:
                    </span>
                    <span className="font-semibold text-[#34065c]">
                      {formatDate(birthApiData?.dateOfBirth || birthDetails?.date)}
                    </span>
                  </div>
                )}
                {(birthApiData?.timeOfBirth || birthDetails?.time) && (
                  <div className="bg-white/80 px-5 py-2.5 rounded-full shadow-[0_2px_10px_rgba(45,170,253,0.15)] border border-[#2DAAFD]/30 flex items-center gap-2 hover:shadow-[0_4px_15px_rgba(45,170,253,0.25)] transition-all duration-300">
                    <span className="text-gray-500 text-xs font-bold uppercase tracking-wider">
                      Time:
                    </span>
                    <span className="font-semibold text-[#34065c]">
                      {formatTime(birthApiData?.timeOfBirth || birthDetails?.time)}
                      {(birthApiData?.timezone || user?.meta?.timezone) && (
                        <span className="ml-1 text-xs text-gray-500">
                          ({formatTimezone(birthApiData?.timezone || user?.meta?.timezone)})
                        </span>
                      )}
                    </span>
                  </div>
                )}
                {(birthApiData?.placeOfBirth || birthDetails?.place) && (
                  <div className="bg-white/80 px-5 py-2.5 rounded-full shadow-[0_2px_10px_rgba(45,170,253,0.15)] border border-[#2DAAFD]/30 flex items-center gap-2 hover:shadow-[0_4px_15px_rgba(45,170,253,0.25)] transition-all duration-300">
                    <span className="text-gray-500 text-xs font-bold uppercase tracking-wider">
                      Place:
                    </span>
                    <span className="font-semibold text-[#34065c]">
                      {birthApiData?.placeOfBirth || birthDetails?.place}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Astro Highlights Cards */}
          {birthApiData && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 w-full">
              {/* Lagna Card */}
              <div className="bg-gradient-to-br from-[#F8FBFF] to-[#E6F3FF] rounded-xl p-6 border border-[#2DAAFD]/30 shadow-md flex flex-col items-center text-center transition-all duration-300">
                <span className="text-black text-[16px] font-bold uppercase tracking-[0.3em] mb-4">
                  LAGNA / ASCENDANT
                </span>
                <div className="space-y-4">
                  <div className="flex flex-col items-center">
                    <span className="text-black text-[12px] uppercase tracking-wider mb-1">
                      Sign :
                    </span>
                    <span className="text-[#34065c] font-spectral text-xl font-bold">
                      {birthApiData.birthDetails?.altAscendant ||
                        birthApiData.personality?.ascendant?.sign ||
                        '--'}
                    </span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-black text-[12px] uppercase tracking-wider mb-1">
                      Lord :
                    </span>
                    <span className="text-[#34065c] font-spectral text-lg font-semibold opacity-90">
                      {birthApiData.birthDetails?.altAscendantLord ||
                        birthApiData.personality?.ascendant?.lord ||
                        '--'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Rashi Card */}
              <div className="bg-gradient-to-br from-[#F8FBFF] to-[#E6F3FF] rounded-xl p-6 border border-[#2DAAFD]/30 shadow-md flex flex-col items-center text-center transition-all duration-300">
                <span className="text-black text-[16px] font-bold uppercase tracking-[0.3em] mb-4">
                  RASHI / MOON SIGN
                </span>
                <div className="space-y-4">
                  <div className="flex flex-col items-center">
                    <span className="text-black text-[12px] uppercase tracking-wider mb-1">
                      Sign :
                    </span>
                    <span className="text-[#34065c] font-spectral text-xl font-bold">
                      {birthApiData.birthDetails?.altSign ||
                        birthApiData.personality?.sunSign?.sign ||
                        '--'}
                    </span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-black text-[12px] uppercase tracking-wider mb-1">
                      Lord :
                    </span>
                    <span className="text-[#34065c] font-spectral text-lg font-semibold opacity-90">
                      {birthApiData.birthDetails?.altSignLord ||
                        birthApiData.personality?.sunSign?.lord ||
                        '--'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Nakshatra Card */}
              <div className="bg-gradient-to-br from-[#F8FBFF] to-[#E6F3FF] rounded-xl p-6 border border-[#2DAAFD]/30 shadow-md flex flex-col items-center text-center transition-all duration-300">
                <span className="text-black text-[16px] font-bold uppercase tracking-[0.3em] mb-4">
                  NAKSHATRA / STAR
                </span>
                <div className="grid grid-cols-2 gap-y-4 gap-x-2 w-full">
                  <div className="flex flex-col items-center">
                    <span className="text-black text-[12px] uppercase tracking-wider mb-1">
                      Name :
                    </span>
                    <span className="text-[#34065c] font-spectral text-lg font-bold">
                      {birthApiData.personality?.nakshatra?.name || '--'}
                    </span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-black text-[12px] uppercase tracking-wider mb-1">
                      Pada :
                    </span>
                    <span className="text-[#34065c] font-spectral text-lg font-bold">
                      {birthApiData.personality?.nakshatra?.charan || '--'}
                    </span>
                  </div>
                  <div className="flex flex-col items-center col-span-2">
                    <span className="text-black text-[12px] uppercase tracking-wider mb-1">
                      Lord :
                    </span>
                    <span className="text-[#34065c] font-spectral text-lg font-semibold opacity-90">
                      {birthApiData.birthDetails?.altNakshatraLord ||
                        birthApiData.personality?.nakshatra?.lord ||
                        '--'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-md py-4 -mx-6 px-6 border-b border-gray-100 shadow-sm transition-all duration-300">
          <TabSlider>
            <button
              onClick={() => setActiveView('basic')}
              className={`
        font-20
        w-full sm:w-auto
        h-10
        px-5
        font-semibold
        font-spectral
        uppercase
        rounded-lg
        flex
        justify-center
        items-center
        cursor-pointer
        inner-shadow-blue
        transition-all
        duration-500
        ease-in-out
        disabled:opacity-60
        ${
          activeView === 'basic'
            ? 'bg-[linear-gradient(to_right,#082261,#34065c)] text-white'
            : 'bg-white text-black hover:bg-[linear-gradient(to_right,#082261,#34065c)] hover:text-white'
        }
      `}
            >
              BASIC CHARTS
            </button>

            <button
              onClick={() => setActiveView('vimshottari')}
              className={`
        font-20
        w-full sm:w-auto
        h-10
        px-5
        font-semibold
        font-spectral
        uppercase
        rounded-lg
        flex
        justify-center
        items-center
        cursor-pointer
        inner-shadow-blue
        transition-all
        duration-500
        ease-in-out
        disabled:opacity-60
        ${
          activeView === 'vimshottari'
            ? 'bg-[linear-gradient(to_right,#082261,#34065c)] text-white'
            : 'bg-white text-black hover:bg-[linear-gradient(to_right,#082261,#34065c)] hover:text-white'
        }
      `}
            >
              VIMSHOTTARI DASHA
            </button>

            <button
              onClick={() => setActiveView('astro')}
              className={`
        font-20
        w-full sm:w-auto
        h-10
        px-5
        font-semibold
        font-spectral
        uppercase
        rounded-lg
        flex
        justify-center
        items-center
        cursor-pointer
        inner-shadow-blue
        transition-all
        duration-500
        ease-in-out
        disabled:opacity-60
        ${
          activeView === 'astro'
            ? 'bg-[linear-gradient(to_right,#082261,#34065c)] text-white'
            : 'bg-white text-black hover:bg-[linear-gradient(to_right,#082261,#34065c)] hover:text-white'
        }
      `}
            >
              ASTRO ENGINE
            </button>

            <button
              onClick={() => {
                setActiveView('Predictions');
                setLoading(true);
              }}
              className={`
    font-20
    w-full sm:w-auto
    h-10
    px-5
    font-semibold
    font-spectral
    uppercase
    rounded-lg
    flex
    justify-center
    items-center
    cursor-pointer
    inner-shadow-blue
    transition-all
    duration-500
    ease-in-out
    disabled:opacity-60
    ${
      activeView === 'Predictions'
        ? 'bg-[linear-gradient(to_right,#082261,#34065c)] text-white'
        : 'bg-white text-black hover:bg-[linear-gradient(to_right,#082261,#34065c)] hover:text-white'
    }
  `}
            >
              Predictions
            </button>

            <button
              onClick={() => setActiveView('General Predictions')}
              className={`
        font-20
        w-full sm:w-auto
        h-10
        px-5
        font-semibold
        font-spectral
        uppercase
        rounded-lg
        flex
        justify-center
        items-center
        cursor-pointer
        inner-shadow-blue
        transition-all
        duration-500
        ease-in-out
        disabled:opacity-60
        ${
          activeView === 'General Predictions'
            ? 'bg-[linear-gradient(to_right,#082261,#34065c)] text-white'
            : 'bg-white text-black hover:bg-[linear-gradient(to_right,#082261,#34065c)] hover:text-white'
        }
      `}
            >
              GENERAL PREDICTIONS
            </button>

            <button
              onClick={() => setActiveView('Ashtakvarga')}
              className={`
        font-20
        w-full sm:w-auto
        h-10
        px-5
        font-semibold
        font-spectral
        uppercase
        rounded-lg
        flex
        justify-center
        items-center
        cursor-pointer
        inner-shadow-blue
        transition-all
        duration-500
        ease-in-out
        disabled:opacity-60
        ${
          activeView === 'Ashtakvarga'
            ? 'bg-[linear-gradient(to_right,#082261,#34065c)] text-white'
            : 'bg-white text-black hover:bg-[linear-gradient(to_right,#082261,#34065c)] hover:text-white'
        }
      `}
            >
              ASHTAKVARGA
            </button>

            <button
              onClick={() => setActiveView('otherCharts')}
              className={`
        font-20
        w-full sm:w-auto
        h-10
        px-5
        font-semibold
        font-spectral
        uppercase
        rounded-lg
        flex
        justify-center
        items-center
        cursor-pointer
        inner-shadow-blue
        transition-all
        duration-500
        ease-in-out
        disabled:opacity-60
        ${
          activeView === 'otherCharts'
            ? 'bg-[linear-gradient(to_right,#082261,#34065c)] text-white'
            : 'bg-white text-black hover:bg-[linear-gradient(to_right,#082261,#34065c)] hover:text-white'
        }
      `}
            >
              Other Charts
            </button>

            <button
              onClick={() => setActiveView('strengths')}
              className={`
        font-20
        w-full sm:w-auto
        h-10
        px-5
        font-semibold
        font-spectral
        uppercase
        rounded-lg
        flex
        justify-center
        items-center
        cursor-pointer
        inner-shadow-blue
        transition-all
        duration-500
        ease-in-out
        disabled:opacity-60
        ${
          activeView === 'strengths'
            ? 'bg-[linear-gradient(to_right,#082261,#34065c)] text-white'
            : 'bg-white text-black hover:bg-[linear-gradient(to_right,#082261,#34065c)] hover:text-white'
        }
      `}
            >
              Strengths
            </button>
          </TabSlider>
        </div>

        {activeView === 'astro' && (
          <div className="space-y-8 border border-[#2DAAFD] rounded-xl p-6 font-lato">
            <DrishtiAspectsPage />
            <PlanetaryPosition />
            <PlanetaryTransit />
            <PlanetaryTransitAspect />
            <LagnaLords />
            <PlanetAwastha />
          </div>
        )}

        {activeView === 'vimshottari' && userId && (
          <div className="space-y-8">
            <VimshottariDasha userId={userId} />
          </div>
        )}

        {activeView === 'basic' && (
          <div className="space-y-8">
            <ChartImages />

            {userId && birthDetails && birthApiData && (
              <PlanetsTable birthDetails={birthDetails} planets={birthApiData.planets ?? []} />
            )}

            {userId && birthDetails && birthApiData && (
              <NavamsaTable birthDetails={birthDetails} navamsa={birthApiData.navamsa ?? []} />
            )}

            {userId && birthDetails && birthApiData && (
              <DasamsaChart birthDetails={birthDetails} dasamsa={birthApiData.dasamsa ?? []} />
            )}
          </div>
        )}

        {activeView === 'otherCharts' && (
          <div className="space-y-8">
            <OtherCharts />
          </div>
        )}

        {activeView === 'General Predictions' && userId && (
          <div className="space-y-8">
            <div className="space-y-8">
              <GenPrediction userId={userId} />
            </div>
          </div>
        )}

        {activeView === 'Predictions' && userId && (
          <div className="space-y-8">
            <Prediction
              fetchedId={userId}
              moonSign={birthApiData?.rashi ?? ''}
              apiData={apiData}
              receivedResponse={!!birthApiData}
              fetchUserType={fetchUserType}
            />
          </div>
        )}

        {activeView === 'Ashtakvarga' && userId && (
          <div className="space-y-8">
            <Ashtakvarga userId={userId} />
          </div>
        )}

        {activeView === 'strengths' && userId && (
          <div className="space-y-8">
            <Strengths userId={userId} />
          </div>
        )}
      </div>
    </>
  );
};
